import { createSelector } from 'reselect';
import get from 'lodash.get';
import { create, auth, roles as rolesApi } from 'services/api';
import produce from 'immer';
import { isAuth0User, getUserId, getUserToken } from 'services/authentication';
import { fetchUser as fetchAuth0User } from 'shared/helpers/auth0';

const RECEIVE = 'create/user/RECEIVE';
const UPDATE_PROFILE = 'create/user/UPDATE_PROFILE';

export const selectUserData = state => get(state, ['v2', 'user'], null);
export const selectUserName = createSelector(
  selectUserData,
  user => get(user, ['name'], 'Missing name')
);
export const selectUserPermissions = createSelector(
  selectUserData,
  user => get(user, ['permissions'], [])
);
export const selectUserAvatar = createSelector(
  selectUserData,
  user => get(user, ['avatar'], {})
);
export const selectUserGroups = createSelector(
  selectUserData,
  user => get(user, ['groups'], [])
);
export const selectUserPreferences = createSelector(
  selectUserData,
  user => get(user, ['preferences'], [])
);
export const selectUserPermissionsV2 = createSelector(
  selectUserData,
  user => get(user, ['self', 'roles'], []).flatMap(({ role }) => role.permissions)
);
export const selectUserShowsV2 = createSelector(
  selectUserData,
  user => get(user, ['self', 'showIds'], [])
);
export const selectUserShows = createSelector(
  selectUserPermissions,
  selectUserShowsV2,
  (permissions, showIds) => [...new Set([...permissions.map(p => p.id), ...showIds])]
);
export const selectHasShows = createSelector(
  selectUserPermissions,
  selectUserShowsV2,
  (permissions, showIds) => !!permissions.length || !!showIds.length
);
export const selectHasFeature = featureName =>
  createSelector(
    selectUserGroups,
    groups =>
      groups.some(group => {
        return group.features.some(feature => feature.name === featureName);
      })
  );
export const selectPreferenceValue = (preferenceName, defaultValue) =>
  createSelector(
    selectUserPreferences,
    preferences => {
      const pref = preferences.find(preference => preference.name === preferenceName);
      if (pref) {
        return pref.value;
      }
      return defaultValue;
    }
  );

export const selectHasRole = roleName =>
  createSelector(
    selectUserData,
    user => {
      const roles = get(user, ['roles'], []);
      return roles.indexOf(roleName) !== -1;
    }
  );

export default function reducer(state = null, action) {
  switch (action.type) {
    case RECEIVE:
      return {
        ...action.payload.createProfile,
        ...action.payload.authUser,
        self: action.payload.rolesUser,
      };
    case UPDATE_PROFILE:
      return action.payload;
    default:
      return state;
  }
}

const normalizeAuthUser = user => {
  if (user['https://acast.com/roles']) {
    return {
      roles: user['https://acast.com/roles'],
      name: user.name,
      email: user.email,
      avatar: {
        original: get(user, 'user_metadata.picture', user.picture),
      },
    };
  }
  return {
    roles: user.roles,
    name: get(user, ['profile', 'name'], null) || get(user, ['providers', '0', 'id'], null),
    email: get(user, ['providers', '0', 'id'], null),
  };
};

export const fetchUser = () => (dispatch, getState) => {
  if (selectUserData(getState())) {
    return Promise.resolve();
  }
  const state = getState();
  const fetchCreateProfile = create.get('/api-v2/user/profile');
  const fetchAuthProfile = isAuth0User(state)
    ? fetchAuth0User(getUserToken(state)).then(user => ({ body: user }))
    : auth.get(`/v1/users/${getUserId(state)}`);
  // Catch errors and fail silently, once the legacy permissions in Create are
  // removed we can delete this.
  const fetchSelf = rolesApi.get('/v1/self').catch(() => ({}));

  return Promise.all([fetchCreateProfile, fetchAuthProfile, fetchSelf]).then(
    ([{ body: createProfile }, { body: authProfile }, { body: rolesUser }]) => {
      const authUser = normalizeAuthUser(authProfile);
      dispatch({
        type: RECEIVE,
        payload: {
          createProfile,
          authUser,
          rolesUser,
        },
      });
    }
  );
};

export const updateProfileAvatar = url => (dispatch, getState) => {
  const state = getState();
  const initialAvatar = selectUserAvatar(state);
  const nextAvatar = produce(initialAvatar, draftAvatar => {
    // eslint-disable-next-line no-param-reassign
    draftAvatar.original = url;
  });

  return create
    .patch('/api-v2/user/profile', { avatar: nextAvatar, modified: new Date().toISOString() })
    .then(payload => {
      dispatch({ type: UPDATE_PROFILE, payload: payload.body });
      return Promise.resolve();
    });
};

export const updatePreference = ({ name, value }) => (dispatch, getState) => {
  const state = getState();
  const initialPref = selectUserPreferences(state);
  const nextPref = produce(initialPref, draftPref => {
    const index = draftPref.findIndex(pref => pref.name === name);
    if (index !== -1) {
      // eslint-disable-next-line no-param-reassign
      draftPref[index].value = value;
    } else {
      draftPref.push({ name, value });
    }
  });

  return create
    .patch('/api-v2/user/profile', { preferences: nextPref, modified: new Date().toISOString() })
    .then(payload => {
      dispatch({ type: UPDATE_PROFILE, payload: payload.body });
      return Promise.resolve();
    });
};
